import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AlertCircle, User, Activity, ClipboardList, Clock, Heart, AlertTriangle, Wind, Utensils, Shield, FileText, Home, ChevronDown, ChevronUp, FileEdit, ChevronsDown, ChevronsUp } from 'lucide-react';
import { FormPageLayout, DocStatusPill } from '../../common';
import type { ActionButtonConfig } from '../../common';
import { CarePlanStatus } from '../../../services/patientmr/careplans.service';
import { useCarePlanDraft } from './hooks/useCarePlanDraft';
import GoalsEditor from './goals/GoalsEditor';
import EquipmentSettingsSection, { emptyEquipmentSettings, normalizeEquipmentSettings, equipmentSettingsErrors } from './sections/EquipmentSettingsSection';
import OrderingPhysicianSection from './sections/OrderingPhysicianSection';
import CertPeriodSection, { applyCertPeriod, hasFixedCertPeriod } from './sections/CertPeriodSection';
import type { CarePlanCertificationPeriod } from '../../../dtos/patientmr/careplans/careplan.dto';
import HomeboundSection, { homeboundJustificationField } from './sections/HomeboundSection';
import MedicalNecessitySection from './sections/MedicalNecessitySection';
import ProviderSection from './sections/ProviderSection';
import type { SectionFieldConfig } from './sections/SectionCard';
import { CarePlanGoal, initialGoals, clearedLegacyGoalFields, goalErrors } from './goals/carePlanGoals';
import { AssessmentsService, AssessmentListItem, AssessmentDiscipline } from '../../../services/patientmr/assessments.service';

interface NursingCarePlanProps {
  editingId?: string | null;
  patientId: string;
  existingData?: any;
  onSave: (data: any, submitAfterSave?: boolean) => void;
  onSubmit?: () => void;
  onReopen?: () => void;
  onApprove?: () => void;
  onCancel: () => void;
  saving?: boolean;
  submitting?: boolean;
  reopening?: boolean;
  carePlanStatus?: CarePlanStatus;
  isReadOnly?: boolean;
  /** Start QA review (SUBMITTED -> QA); shown only when provided */
  onStartQA?: () => void;
  startingQA?: boolean;
  /** Extra header content (version badge, version picker) */
  headerExtra?: React.ReactNode;
  /** Saves a draft without closing the form (autosave) */
  onAutosave?: (data: Record<string, unknown>) => Promise<void>;
  /** Autosave runs only while true */
  autosaveEnabled?: boolean;
  /** Phase 19 F26: the plan record's certification period; its dates are shown read-only */
  certificationPeriod?: CarePlanCertificationPeriod | null;
}

interface CheckboxFieldProps {
  field: string;
  label: string;
  checked: boolean;
  onChange: (field: string, value: boolean) => void;
  isReadOnly?: boolean;
}

const CheckboxField = React.memo(({ field, label, checked, onChange, isReadOnly = false }: CheckboxFieldProps) => (
  <label className={`flex items-center space-x-2 text-sm text-gray-700 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(field, e.target.checked)}
      disabled={isReadOnly}
      className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
    />
    <span>{label}</span>
  </label>
));

// Phase 19 F25: per-plan copy for the shared sections (keys unchanged).
const RN_PHYSICIAN_OVERRIDES = {
  orderingPhysicianName: { label: 'Physician Name', placeholder: 'Enter ordering physician name' },
  orderingPhysicianNPI: { label: 'NPI Number' },
  orderingPhysicianPhone: { label: 'Phone Number', required: true, placeholder: '(555) 555-5555' },
  orderingPhysicianSpecialty: { required: true, placeholder: 'e.g., Pediatrician, Pulmonologist' },
  orderingPhysicianFax: { label: 'Fax Number', placeholder: '(713) 555-2001' },
  orderingPhysicianAddressStreet: { label: 'Address', placeholder: 'Street address' },
};

const RN_MEDICAL_NECESSITY_GUIDANCE = {
  heading: 'Must include:',
  items: [
    'Clinical complexity (diagnoses, equipment, medical fragility)',
    'Skilled nursing requirements (specific RN-only tasks)',
    'Home environment factors (caregiver limits, other children, work)',
    'Risk without PDN (life-threatening complications)',
    'Modifier UA justification (if trach/vent specialized care)',
  ],
};

const RN_MEDICAL_NECESSITY_FIELDS: SectionFieldConfig[] = [
  { key: 'medicalNecessityJustification', label: 'Medical necessity justification', srOnlyLabel: true, kind: 'textarea', rows: 8, placeholder: 'This patient requires specialized Private Duty Nursing services due to...' },
];

const RN_HOMEBOUND_FIELDS: SectionFieldConfig[] = [
  { key: 'homeboundIndicators', label: 'Homebound Indicators', kind: 'checkboxes', items: [
    { key: 'homeboundLeavesHomeInfrequently', label: 'Patient leaves home infrequently due to medical condition' },
    { key: 'homeboundRequiresConsiderableEffort', label: 'Leaving home requires considerable and taxing effort' },
    { key: 'homeboundRequiresSupportiveDevices', label: 'Requires supportive devices (wheelchair, portable ventilator, oxygen)' },
    { key: 'homeboundRequiresSpecialTransport', label: 'Requires special medical transportation' },
    { key: 'homeboundRequiresAssistance', label: 'Requires assistance of another person to leave home' },
    { key: 'homeboundTechnologyDependent', label: 'Technology-dependent with life-sustaining equipment' },
  ] },
  homeboundJustificationField({ required: true, placeholder: 'Explain why patient is confined to home. Include: equipment dependencies, medical fragility, effort/risk of leaving home, need for continuous monitoring.' }),
];

const NursingCarePlanForm: React.FC<NursingCarePlanProps> = ({
  editingId,
  patientId,
  existingData,
  onSave,
  onReopen,
  onApprove,
  onCancel,
  saving = false,
  submitting = false,
  reopening = false,
  carePlanStatus = CarePlanStatus.DRAFT,
  isReadOnly = false,
  onStartQA,
  startingQA = false,
  headerExtra,
  onAutosave,
  autosaveEnabled = false,
  certificationPeriod = null
}) => {
  // Form configuration for patient type - MVP defaults to pediatric PDN
  const [formConfig, setFormConfig] = useState({
    patientType: 'pediatric' as 'pediatric' | 'adult',
    isPDN: true,
  });

  // Assessment list state
  const [assessmentsList, setAssessmentsList] = useState<AssessmentListItem[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState<boolean>(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  const [formData, setFormData] = useState({
    // Phase 19 F12: every goal of the plan (domain, measure, target date, status, progress)
    goals: [] as CarePlanGoal[],
    // Phase 19 F13: ventilator / oxygen / trach / feeding-pump settings printed on the POC
    equipmentSettings: emptyEquipmentSettings(),
    // === ASSESSMENT REFERENCE ===
    assessmentId: '',
    assessmentDate: '',
    assessedBy: '',
    assessmentType: '',
    
    // === ORDERING PHYSICIAN (WHO SIGNS THIS POC) ===
    orderingPhysicianName: '',
    orderingPhysicianNPI: '',
    orderingPhysicianPhone: '',
    orderingPhysicianFax: '',
    orderingPhysicianSpecialty: '',
    orderingPhysicianAddressStreet: '',
    orderingPhysicianAddressCity: '',
    orderingPhysicianAddressState: '',
    orderingPhysicianAddressZip: '',
    physicianReceivedPOCDate: '',
    
    // === CERTIFICATION PERIOD (CMS-485) ===
    certificationPeriodFrom: '',
    certificationPeriodTo: '',
    
    // === CLINICAL STATUS ===
    prognosis: '',
    rehabilitationPotential: '',
    
    // === MEDICAL NECESSITY (CRITICAL FOR PA/485) ===
    medicalNecessityJustification: '',
    
    // === HOMEBOUND STATUS (CRITICAL FOR 485) ===
    homeboundLeavesHomeInfrequently: false,
    homeboundRequiresConsiderableEffort: false,
    homeboundRequiresSupportiveDevices: false,
    homeboundRequiresSpecialTransport: false,
    homeboundRequiresAssistance: false,
    homeboundTechnologyDependent: false,
    homeboundJustification: '',
    
    // === ACTIVITIES PERMITTED ===
    activityCompleteBedrest: false,
    activityBedrestBRP: false,
    activityUpAsTolerated: false,
    activityTransferBedChair: false,
    activityExercisesPrescribed: false,
    activityPartialWeightBearing: false,
    activityIndependentAtHome: false,
    activityCrutches: false,
    activityCane: false,
    activityWheelchair: false,
    activityWalker: false,
    activityNoRestrictions: false,
    activityOther: false,
    activityOtherSpecify: '',
    activityRestrictionsNarrative: '',
    
    // === PEDIATRIC PDN - TRACHEOSTOMY CARE PLAN ===
    pdnTracheostomyCarePlan: '',
    pdnTracheostomyChangeSchedule: '',
    pdnTracheostomySuctioningPlan: '',
    pdnTracheostomyEmergencyProtocol: '',
    pdnTracheostomyGoal: '',
    pdnTracheostomyIntervention: '',
    
    // === PEDIATRIC PDN - VENTILATOR CARE PLAN ===
    pdnVentilatorCarePlan: '',
    pdnVentilatorWeaningPlan: '',
    pdnVentilatorAlarmResponse: '',
    pdnVentilatorGoal: '',
    pdnVentilatorIntervention: '',
    
    // === PEDIATRIC PDN - RESPIRATORY CARE ===
    pdnRespiratoryTreatments: '',
    pdnOxygenManagement: '',
    pdnRespiratoryGoal: '',
    pdnRespiratoryIntervention: '',
    
    // === PEDIATRIC PDN - FEEDING/NUTRITION ===
    pdnFeedingMethod: '',
    pdnFeedingSchedule: '',
    pdnFormulaType: '',
    pdnFeedingRate: '',
    pdnGtubeCarePlan: '',
    pdnFeedingGoal: '',
    pdnFeedingIntervention: '',
    
    // === PEDIATRIC PDN - SEIZURE MANAGEMENT ===
    pdnSeizureProtocol: '',
    pdnSeizureRescuePlan: '',
    pdnSeizureGoal: '',
    pdnSeizureIntervention: '',
    
    // === PEDIATRIC PDN - MEDICATION MANAGEMENT ===
    pdnMedicationAdministrationPlan: '',
    pdnMedicationGoal: '',
    pdnMedicationIntervention: '',
    
    // === PEDIATRIC PDN - SKILLED NURSING HOURS ===
    pdnHoursPerDay: '',
    pdnDaysPerWeek: '',
    pdnTotalWeeklyHours: '',
    pdnHoursJustification: '',
    // Phase 19 F14: F00120 §3 "plan to decrease hours or discharge from service"
    hoursTransitionPlan: '',
    // Phase 19 F14: F00120 §1 priority problems (two to four, one per line)
    priorityProblems: '',
    pdnShiftSchedule: '',
    pdnNightCoverageRequired: false,
    pdnWeekendCoverageRequired: false,
    
    // === PEDIATRIC PDN - CAREGIVER/FAMILY TRAINING ===
    pdnCaregiverTrainingPlan: '',
    pdnBackupCaregiverPlan: '',
    pdnCaregiverEducationGoal: '',
    pdnCaregiverEducationIntervention: '',
    
    // === PEDIATRIC PDN - EMERGENCY PREPAREDNESS ===
    pdnEmergencyContactList: '',
    pdnPowerOutagePlan: '',
    pdnEquipmentFailurePlan: '',
    pdnEvacuationPlan: '',
    pdnHospitalGoKit: false,
    // T-01B NET-NEW FIELD — used in Nursing Addendum Section D
    contingencyPlan: '',
    
    // === NURSING GOALS ===
    goalSelfCare: '',
    goalSelfCareType: 'short_term' as 'short_term' | 'long_term',
    goalSelfCareTargetDate: '',
    goalSelfCareMeasurable: '',
    goalMedication: '',
    goalMedicationType: 'short_term' as 'short_term' | 'long_term',
    goalMedicationTargetDate: '',
    goalMedicationMeasurable: '',
    goalSafety: '',
    goalSafetyType: 'short_term' as 'short_term' | 'long_term',
    goalSafetyTargetDate: '',
    goalSafetyMeasurable: '',
    goalEducation: '',
    goalEducationType: 'short_term' as 'short_term' | 'long_term',
    goalEducationTargetDate: '',
    goalEducationMeasurable: '',
    goalWoundCare: '',
    goalWoundCareType: 'short_term' as 'short_term' | 'long_term',
    goalWoundCareTargetDate: '',
    goalWoundCareMeasurable: '',
    goalPainManagement: '',
    goalPainManagementType: 'short_term' as 'short_term' | 'long_term',
    goalPainManagementTargetDate: '',
    goalPainManagementMeasurable: '',
    goalNutrition: '',
    goalNutritionType: 'short_term' as 'short_term' | 'long_term',
    goalNutritionTargetDate: '',
    goalNutritionMeasurable: '',
    goalOther: '',
    goalOtherType: 'short_term' as 'short_term' | 'long_term',
    goalOtherTargetDate: '',
    goalOtherMeasurable: '',
    
    // === NURSING INTERVENTIONS ===
    interventionSelfCare: '',
    interventionMedication: '',
    interventionSafety: '',
    interventionEducation: '',
    
    // === SKILLED NURSING ORDERS ===
    snFrequency: '',
    snFrequencyCustom: '',
    snDuration: '',
    snDurationCustom: '',
    snTasks: '',
    
    // === OTHER DISCIPLINES ORDERED ===
    ptFrequency: '',
    otFrequency: '',
    stFrequency: '',
    
    // === SAFETY MEASURES ===
    safetyFallPrecautions: false,
    safetySeizurePrecautions: false,
    safetyAspirationPrecautions: false,
    safetyInfectionControl: false,
    safetySupervisedMedications: false,
    safetyOxygenSafety: false,
    safetyEmergencyPlan: false,
    safetyTracheostomyPrecautions: false,
    safetyVentilatorPrecautions: false,
    safetyChildproofing: false,
    safetySupervisedAtAllTimes: false,
    safetyNoUnsupervisedBathing: false,
    safetyPositioningProtocol: false,
    safetySkinIntegrityProtocol: false,
    safetyOther: '',
    
    // === NUTRITIONAL REQUIREMENTS ===
    feedingMethod: '',
    
    // === DISCHARGE PLANNING ===
    dischargePlanning: '',
    dischargeGoals: '',
    
    // === PROVIDER INFORMATION ===
    providerName: '',
    providerCredentials: '',
    dateCreated: new Date().toISOString().split('T')[0],
    
    // === ADDITIONAL NOTES ===
    additionalNotes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Collapsible sections state
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'Care Plan Config': false,
    'Clinical Status': true,
    'Medical Necessity': true,
    'Ordering Physician': true,
    'Homebound Status': true,
    'Activities & Safety': true,
    'PDN Tracheostomy': true,
    'PDN Ventilator': true,
    'PDN Seizure': true,
    'PDN Feeding': true,
    'PDN Hours': true,
    'PDN Caregiver Training': true,
    'PDN Emergency': true,
    'Nursing Goals': true,
    'Nursing Interventions': true,
    'Skilled Nursing Orders': true,
    'Other Disciplines': true,
    'Safety Measures': true,
    'Discharge Planning': true,
    'Provider Information': true,
    'Additional Notes': true
  });
  const allSectionsCollapsed = useMemo(
    () => Object.values(collapsedSections).every(v => v === true),
    [collapsedSections]
  );

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const toggleAllSections = useCallback((collapse: boolean) => {
    const newState: any = {};
    Object.keys(collapsedSections).forEach(key => {
      newState[key] = collapse;
    });
    setCollapsedSections(newState);
    setConfigSectionCollapsed(collapse);
  }, [collapsedSections]);

  useEffect(() => {
    if (existingData) {
      setFormData(prev => ({ ...prev, ...existingData, goals: initialGoals('RN', existingData), equipmentSettings: normalizeEquipmentSettings(existingData.equipmentSettings) }));
      if (existingData.formConfig) {
        setFormConfig(existingData.formConfig);
      }
    }
  }, [existingData]);

  // Phase 19 F26: a plan with a certification period stores its dates in the cert period keys
  useEffect(() => {
    if (hasFixedCertPeriod(certificationPeriod)) setFormData(prev => applyCertPeriod(prev, certificationPeriod));
  }, [certificationPeriod]);

  // Auto-calculate total weekly hours
  useEffect(() => {
    const hoursPerDay = parseInt(formData.pdnHoursPerDay) || 0;
    const daysPerWeek = parseInt(formData.pdnDaysPerWeek) || 0;
    const total = hoursPerDay * daysPerWeek;
    if (total > 0) {
      setFormData(prev => ({ ...prev, pdnTotalWeeklyHours: total.toString() }));
    }
  }, [formData.pdnHoursPerDay, formData.pdnDaysPerWeek]);

  const handleConfigChange = (key: string, value: boolean | string) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  // Load assessments when dropdown is opened
  const loadAssessments = async () => {
    // Skip loading if assessments already loaded (to save API costs)
    if (assessmentsList.length > 0) return;
    
    try {
      setLoadingAssessments(true);
      const result = await AssessmentsService.getAssessments(patientId);
      // Filter assessments to only show NURSING discipline
      const nursingAssessments = result.assessments.filter(
        assessment => assessment.discipline === AssessmentDiscipline.RN
      );
      setAssessmentsList(nursingAssessments);
    } catch (error) {
      console.error('Error loading assessments:', error);
    } finally {
      setLoadingAssessments(false);
    }
  };

  // Helper function to format assessment type for display
  const formatAssessmentType = (assessmentType: string | undefined): string => {
    if (!assessmentType) return 'N/A';
    
    // Handle backend assessment types with comprehensive mapping
    const typeMap: Record<string, string> = {
      'START_OF_CARE': 'Start of Care',
      'INITIAL': 'Start of Care',
      'initial': 'Start of Care',
      'REASSESSMENT': 'Recertification',
      'REASSESSMEMT': 'Recertification', // Handle potential typo
      'recertification': 'Recertification',
      'END_OF_CARE': 'Discharge',
      'discharge': 'Discharge'
    };
    
    return typeMap[assessmentType] || assessmentType;
  };

  // Handle assessment selection
  const handleAssessmentSelection = (assessmentId: string) => {
    const selectedAssessment = assessmentsList.find(a => a.assessmentId === assessmentId);
    
    if (selectedAssessment) {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: selectedAssessment.date ? new Date(selectedAssessment.date).toISOString().split('T')[0] : '',
        assessedBy: selectedAssessment.providerName || '',
        assessmentType: selectedAssessment.assessmentType || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: '',
        assessedBy: '',
        assessmentType: ''
      }));
    }
  };

  // getStatusColor / getStatusDisplayText removed — use <DocStatusPill /> from common/Pills

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    Object.assign(newErrors, goalErrors(formData.goals));
    if (isPediatricPDN && !formData.hoursTransitionPlan.trim()) newErrors.hoursTransitionPlan = 'Plan to decrease hours or discharge is required for PDN (F00120 Section 3)';
    Object.assign(newErrors, equipmentSettingsErrors(formData.equipmentSettings, {
      hasVentPlan: Boolean(formData.pdnVentilatorCarePlan.trim()) || formData.safetyVentilatorPrecautions,
      hasOxygenPlan: Boolean(formData.pdnOxygenManagement.trim()) || formData.safetyOxygenSafety,
    }));
    if (!formData.certificationPeriodFrom) newErrors.certificationPeriodFrom = 'Certification start date is required';
    if (!formData.certificationPeriodTo) newErrors.certificationPeriodTo = 'Certification end date is required';
    if (!formData.prognosis) newErrors.prognosis = 'Prognosis is required';
    if (!formData.rehabilitationPotential) newErrors.rehabilitationPotential = 'Rehabilitation potential is required';
    if (!formData.interventionSelfCare.trim()) newErrors.interventionSelfCare = 'At least one intervention is required';
    if (!formData.snFrequency) newErrors.snFrequency = 'Visit frequency is required';
    if (!formData.snDuration) newErrors.snDuration = 'Treatment duration is required';
    if (!formData.providerName.trim()) newErrors.providerName = 'Provider name is required';
    
    // Assessment reference
    if (!formData.assessmentId) newErrors.assessmentId = 'Assessment reference is required';
    
    // Medical necessity
    if (!formData.medicalNecessityJustification.trim()) {
      newErrors.medicalNecessityJustification = 'Medical necessity justification required for PA';
    }
    
    // Homebound
    if (!formData.homeboundJustification.trim()) {
      newErrors.homeboundJustification = 'Homebound justification required for 485';
    }
    
    // Physician
    if (!formData.orderingPhysicianName.trim()) {
      newErrors.orderingPhysicianName = 'Ordering physician name is required';
    }
    if (!formData.orderingPhysicianNPI.trim()) {
      newErrors.orderingPhysicianNPI = 'Physician NPI required for 485';
    }
    
    if (formConfig.isPDN) {
      if (!formData.pdnHoursPerDay) newErrors.pdnHoursPerDay = 'PDN hours per day is required';
      // Days per week
      if (!formData.pdnDaysPerWeek) {
        newErrors.pdnDaysPerWeek = 'Days per week is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (submitAfterSave = false) => {
    // Phase 4: Save keeps a DRAFT without required-field checks; Submit validates first
    if (!submitAfterSave || validateForm()) {
      onSave({ ...formData, ...clearedLegacyGoalFields('RN'), goals: formData.goals, formConfig, status: 'Draft' }, submitAfterSave);
    }
  };

  const handleChange = useCallback((field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: '' };
    });
  }, []);

  const isPediatricPDN = formConfig.patientType === 'pediatric' && formConfig.isPDN;

  const isAnyLoading = saving || submitting || reopening || startingQA;
  const isDraft = !carePlanStatus || carePlanStatus === CarePlanStatus.DRAFT;
  const isSubmittedOrQA = carePlanStatus === CarePlanStatus.SUBMITTED || carePlanStatus === CarePlanStatus.QA;

  // Phase 4 (ASM-03): dirty tracking, autosave while DRAFT, unsaved-changes guard
  const draft = useCarePlanDraft({
    getSnapshot: () => JSON.stringify({ formData, formConfig }),
    getSaveData: () => ({ ...formData, ...clearedLegacyGoalFields('RN'), goals: formData.goals, formConfig, status: 'Draft' }),
    onAutosave,
    enabled: autosaveEnabled && isDraft && !isReadOnly && !!editingId,
    resetKey: `${editingId ?? 'new'}:${carePlanStatus}`,
  });
  const handleClose = () => draft.confirmLeave(onCancel);
  const isComplete = carePlanStatus === CarePlanStatus.COMPLETE;

  const actions: ActionButtonConfig[] = [
    { key: 'cancel', label: 'Close', onClick: handleClose, variant: 'secondary', disabled: isAnyLoading },
    { key: 'save', label: 'Save', loadingLabel: editingId ? 'Updating...' : 'Saving...', onClick: () => handleSave(false), variant: 'primary', disabled: isAnyLoading, loading: saving, hidden: !isDraft || isReadOnly },
    { key: 'submit', label: 'Submit', loadingLabel: 'Submitting...', onClick: () => handleSave(true), variant: 'primary', disabled: isAnyLoading, loading: submitting, hidden: !isDraft || isReadOnly },
    { key: 'reject', label: 'Reject', loadingLabel: 'Rejecting...', onClick: onReopen ?? (() => {}), variant: 'danger', disabled: isAnyLoading, loading: reopening, hidden: !isSubmittedOrQA || !onReopen || isReadOnly },
    { key: 'startQA', label: 'Start QA', loadingLabel: 'Starting QA...', onClick: onStartQA ?? (() => {}), variant: 'warning', disabled: isAnyLoading, loading: startingQA, hidden: carePlanStatus !== CarePlanStatus.SUBMITTED || !onStartQA || isReadOnly },
    { key: 'approve', label: 'Approve', loadingLabel: 'Approving...', onClick: onApprove ?? (() => {}), variant: 'success', disabled: isAnyLoading, loading: submitting, hidden: !isSubmittedOrQA || !onApprove || isReadOnly },
    { key: 'reopen', label: 'Reopen', loadingLabel: 'Reopening...', onClick: onReopen ?? (() => {}), variant: 'primary', disabled: isAnyLoading, loading: reopening, hidden: !isComplete || !onReopen || isReadOnly, className: 'bg-blue-600 hover:bg-blue-700' },
  ];

  return (
    <FormPageLayout
      onBack={handleClose}
      actions={actions}
      title="Nursing Care Plan"
      headerRight={
        <div className="flex items-center gap-2">
          {draft.indicator}
          {headerExtra}
          <DocStatusPill status={carePlanStatus} />
          <button
            type="button"
            onClick={() => toggleAllSections(!allSectionsCollapsed)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md border border-slate-200 transition-colors"
          >
            {allSectionsCollapsed ? (
              <><ChevronsDown size={14} /><span>Expand All</span></>
            ) : (
              <><ChevronsUp size={14} /><span>Collapse All</span></>
            )}
          </button>
        </div>
      }
    >
      <div {...draft.captureProps}>
      <div className="relative">

        {/* Form Configuration */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 text-left">
          <div 
            className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
            onClick={() => setConfigSectionCollapsed(!configSectionCollapsed)}
          >
            <div className="flex items-center gap-3">
              <FileEdit size={18} className="text-blue-600" />
              <div>
                <h4 className="text-md font-medium text-gray-700">Care Plan Configuration</h4>
                {configSectionCollapsed && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formConfig.patientType.charAt(0).toUpperCase() + formConfig.patientType.slice(1)} • {formConfig.isPDN ? 'PDN' : 'Standard'}
                  </p>
                )}
              </div>
            </div>
            <div>
              {configSectionCollapsed ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
            </div>
          </div>

          <div className={configSectionCollapsed ? 'hidden' : 'p-4 border-t border-gray-200'}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Patient Type</label>
              <select
                disabled={isReadOnly}
                className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                value={formConfig.patientType}
                onChange={(e) => handleConfigChange('patientType', e.target.value)}
              >
                <option value="pediatric">Pediatric</option>
                <option value="adult">Adult</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  disabled={isReadOnly}
                  className={`mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                  checked={formConfig.isPDN}
                  onChange={(e) => handleConfigChange('isPDN', e.target.checked)}
                />
                <span>Private Duty Nursing (PDN)</span>
              </label>
            </div>
          </div>
          <div className="mt-6">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Assessment Reference</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Nursing Assessment *</label>
                <select
                  ref={selectRef}
                  value={formData.assessmentId}
                  onChange={(e) => handleAssessmentSelection(e.target.value)}
                  onFocus={loadAssessments}
                  className={`w-full px-2 py-2 text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.assessmentId ? 'border-red-500' : 'border-gray-300'} max-w-full ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  disabled={loadingAssessments || isReadOnly}
                >
                  <option value="">{loadingAssessments ? 'Loading nursing assessments...' : 'Select a nursing assessment'}</option>
                  {assessmentsList.map((assessment) => (
                    <option key={assessment.assessmentId} value={assessment.assessmentId}>
                      {formatAssessmentType(assessment.assessmentType)} | {new Date(assessment.date).toLocaleDateString()} | {assessment.providerName}
                    </option>
                  ))}
                </select>
                {errors.assessmentId && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.assessmentId}</p>}
              </div>
              
              {/* Selected Assessment Details */}
              {formData.assessmentId && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h6 className="text-sm font-semibold text-blue-900 mb-2">Selected Assessment Details</h6>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Assessment ID:</span>
                      <p className="text-gray-900 mt-0.5">{formData.assessmentId}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Type:</span>
                      <p className="text-gray-900 mt-0.5">
                        {formatAssessmentType(formData.assessmentType)}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Assessment Date:</span>
                      <p className="text-gray-900 mt-0.5">
                        {formData.assessmentDate ? new Date(formData.assessmentDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Assessed By:</span>
                      <p className="text-gray-900 mt-0.5">{formData.assessedBy || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {isPediatricPDN && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start">
              <Heart size={18} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <strong>Pediatric PDN Care Plan:</strong> Includes tracheostomy, ventilator, seizure management, and caregiver training sections required for Texas Medicaid PDN authorization.
              </div>
            </div>
          )}
          <CertPeriodSection fixedPeriod={certificationPeriod} values={formData} onChange={handleChange} errors={errors} readOnly={isReadOnly} />
          </div>
        </div>

        {/* Form Content */}
        <div className="space-y-6 text-left">
          {/* ========== ORDERING PHYSICIAN ========== */}
          <OrderingPhysicianSection
            title="Ordering Physician (Required for CMS-485)"
            overrides={RN_PHYSICIAN_OVERRIDES}
            showReceivedPOCDate
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections['Ordering Physician']}
            onToggle={() => toggleSection('Ordering Physician')}
          />

          {/* ========== CLINICAL STATUS ========== */}
          <div className="bg-amber-50 rounded-lg border border-amber-200">
            <div 
              className="flex items-center justify-between cursor-pointer p-4 hover:bg-amber-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Clinical Status')}
            >
              <div className="flex items-center gap-3">
                <Activity size={18} className="text-amber-600" />
                <div>
                  <h4 className="text-md font-medium text-amber-900">Clinical Status</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Clinical Status'] ? <ChevronDown size={20} className="text-amber-600" /> : <ChevronUp size={20} className="text-amber-600" />}
              </div>
            </div>

            <div className={collapsedSections['Clinical Status'] ? 'hidden' : 'p-4 border-t border-amber-200'}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prognosis *</label>
                <select value={formData.prognosis} onChange={(e) => handleChange('prognosis', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.prognosis ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select prognosis</option>
                  <option value="Poor">Poor</option>
                  <option value="Guarded">Guarded</option>
                  <option value="Fair">Fair</option>
                  <option value="Good">Good</option>
                  <option value="Excellent">Excellent</option>
                </select>
                {errors.prognosis && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.prognosis}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rehabilitation Potential *</label>
                <select value={formData.rehabilitationPotential} onChange={(e) => handleChange('rehabilitationPotential', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.rehabilitationPotential ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select potential</option>
                  <option value="None">None</option>
                  <option value="Minimal">Minimal</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Good">Good</option>
                  <option value="Excellent">Excellent</option>
                </select>
                {errors.rehabilitationPotential && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.rehabilitationPotential}</p>}
              </div>
            </div>
            </div>
          </div>

          {/* ========== MEDICAL NECESSITY ========== */}
          <MedicalNecessitySection
            title="Medical Necessity Justification (Required for PA & CMS-485)"
            guidance={RN_MEDICAL_NECESSITY_GUIDANCE}
            fields={RN_MEDICAL_NECESSITY_FIELDS}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections['Medical Necessity']}
            onToggle={() => toggleSection('Medical Necessity')}
          />

          {/* ========== HOMEBOUND STATUS (CMS-485 Section 7) ========== */}
          <HomeboundSection
            title="Homebound Status (Required for CMS-485)"
            fields={RN_HOMEBOUND_FIELDS}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections['Homebound Status']}
            onToggle={() => toggleSection('Homebound Status')}
          />

          {/* ========== ACTIVITIES PERMITTED & SAFETY PRECAUTIONS (CMS-485 Section 8 & 9) ========== */}
          <div className="bg-blue-50 rounded-lg border border-blue-200">
            <div 
              className="flex items-center justify-between cursor-pointer p-4 hover:bg-blue-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Activities & Safety')}
            >
              <div className="flex items-center gap-3">
                <Activity size={18} className="text-blue-600" />
                <div>
                  <h4 className="text-md font-medium text-blue-900">Activities Permitted & Safety Precautions</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Activities & Safety'] ? <ChevronDown size={20} className="text-blue-600" /> : <ChevronUp size={20} className="text-blue-600" />}
              </div>
            </div>

            <div className={collapsedSections['Activities & Safety'] ? 'hidden' : 'p-4 border-t border-blue-200'}>
            
            {/* Activity Level */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Activities Permitted</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityCompleteBedrest} onChange={e => handleChange('activityCompleteBedrest', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Complete Bedrest</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityBedrestBRP} onChange={e => handleChange('activityBedrestBRP', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Bedrest BRP</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityUpAsTolerated} onChange={e => handleChange('activityUpAsTolerated', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Up As Tolerated</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityTransferBedChair} onChange={e => handleChange('activityTransferBedChair', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Transfer Bed/Chair</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityExercisesPrescribed} onChange={e => handleChange('activityExercisesPrescribed', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Exercises Prescribed</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityPartialWeightBearing} onChange={e => handleChange('activityPartialWeightBearing', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Partial Weight Bearing</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityIndependentAtHome} onChange={e => handleChange('activityIndependentAtHome', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Independent At Home</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityCrutches} onChange={e => handleChange('activityCrutches', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Crutches</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityCane} onChange={e => handleChange('activityCane', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Cane</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityWheelchair} onChange={e => handleChange('activityWheelchair', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Wheelchair</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityWalker} onChange={e => handleChange('activityWalker', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Walker</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityNoRestrictions} onChange={e => handleChange('activityNoRestrictions', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>No Restrictions</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" disabled={isReadOnly} checked={formData.activityOther} onChange={e => handleChange('activityOther', e.target.checked)}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className={`text-sm ${isReadOnly ? 'text-gray-500' : ''}`}>Other (Specify)</span>
                </label>
              </div>
              {formData.activityOther && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Activity (Specify)</label>
                  <input type="text" disabled={isReadOnly} value={formData.activityOtherSpecify} onChange={e => handleChange('activityOtherSpecify', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="Specify other activity" />
                </div>
              )}
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Activity Restrictions & Instructions</label>
                <textarea disabled={isReadOnly} value={formData.activityRestrictionsNarrative} onChange={e => handleChange('activityRestrictionsNarrative', e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="Describe activity restrictions. E.g., Patient is technology-dependent (ventilator and tracheostomy). Mobility via wheelchair with assistance. Seizure precautions at all times. Cannot be left unattended." />
              </div>
            </div>

            {/* Safety Precautions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Safety Precautions</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyFallPrecautions} onChange={e => handleChange('safetyFallPrecautions', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Fall</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetySeizurePrecautions} onChange={e => handleChange('safetySeizurePrecautions', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Seizure</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyAspirationPrecautions} onChange={e => handleChange('safetyAspirationPrecautions', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Aspiration</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyInfectionControl} onChange={e => handleChange('safetyInfectionControl', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Infection control</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetySupervisedMedications} onChange={e => handleChange('safetySupervisedMedications', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Supervised meds</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyOxygenSafety} onChange={e => handleChange('safetyOxygenSafety', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Oxygen safety</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyEmergencyPlan} onChange={e => handleChange('safetyEmergencyPlan', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Emergency plan</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyTracheostomyPrecautions} onChange={e => handleChange('safetyTracheostomyPrecautions', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Tracheostomy</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyVentilatorPrecautions} onChange={e => handleChange('safetyVentilatorPrecautions', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Ventilator</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyChildproofing} onChange={e => handleChange('safetyChildproofing', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Childproofing</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetySupervisedAtAllTimes} onChange={e => handleChange('safetySupervisedAtAllTimes', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Supervised at all times</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyNoUnsupervisedBathing} onChange={e => handleChange('safetyNoUnsupervisedBathing', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">No unsupervised bathing</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetyPositioningProtocol} onChange={e => handleChange('safetyPositioningProtocol', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Positioning protocol</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" checked={formData.safetySkinIntegrityProtocol} onChange={e => handleChange('safetySkinIntegrityProtocol', e.target.checked)}
                    disabled={isReadOnly}
                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
                  <span className="text-sm">Skin integrity protocol</span>
                </label>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Other Precautions</label>
                <input type="text" value={formData.safetyOther} onChange={e => handleChange('safetyOther', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  placeholder="Specify other precautions" />
              </div>
            </div>
            </div>
          </div>

          {/* ========== PDN TRACHEOSTOMY CARE ========== */}
          {isPediatricPDN && (
            <div className="bg-red-50 rounded-lg border border-red-200">
              <div 
                className="flex items-center justify-between cursor-pointer p-4 hover:bg-red-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Tracheostomy')}
              >
                <div className="flex items-center gap-3">
                  <Heart size={18} className="text-red-600" />
                  <div>
                    <h4 className="text-md font-medium text-red-900">Tracheostomy Care Plan</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Tracheostomy'] ? <ChevronDown size={20} className="text-red-600" /> : <ChevronUp size={20} className="text-red-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Tracheostomy'] ? 'hidden' : 'p-4 border-t border-red-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tracheostomy Care Plan</label>
                  <textarea value={formData.pdnTracheostomyCarePlan} onChange={(e) => handleChange('pdnTracheostomyCarePlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                    placeholder="Detailed tracheostomy care procedures and protocols" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trach Change Schedule</label>
                  <input type="text" value={formData.pdnTracheostomyChangeSchedule} onChange={(e) => handleChange('pdnTracheostomyChangeSchedule', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., Weekly on Mondays or PRN" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suctioning Plan</label>
                  <textarea value={formData.pdnTracheostomySuctioningPlan} onChange={(e) => handleChange('pdnTracheostomySuctioningPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Suctioning frequency, depth, technique" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trach Emergency Protocol</label>
                  <textarea value={formData.pdnTracheostomyEmergencyProtocol} onChange={(e) => handleChange('pdnTracheostomyEmergencyProtocol', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Steps for accidental decannulation, obstruction" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tracheostomy Interventions</label>
                  <textarea value={formData.pdnTracheostomyIntervention} onChange={(e) => handleChange('pdnTracheostomyIntervention', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Specific nursing interventions for trach care" />
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ========== PDN VENTILATOR CARE ========== */}
          {isPediatricPDN && (
            <div className="bg-orange-50 rounded-lg border border-orange-200">
              <div 
                className="flex items-center justify-between cursor-pointer p-4 hover:bg-orange-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Ventilator')}
              >
                <div className="flex items-center gap-3">
                  <Wind size={18} className="text-orange-600" />
                  <div>
                    <h4 className="text-md font-medium text-orange-900">Ventilator Care Plan</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Ventilator'] ? <ChevronDown size={20} className="text-orange-600" /> : <ChevronUp size={20} className="text-orange-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Ventilator'] ? 'hidden' : 'p-4 border-t border-orange-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ventilator Care Plan</label>
                  <textarea value={formData.pdnVentilatorCarePlan} onChange={(e) => handleChange('pdnVentilatorCarePlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                    placeholder="Ventilator management protocols and monitoring" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weaning Plan</label>
                  <textarea value={formData.pdnVentilatorWeaningPlan} onChange={(e) => handleChange('pdnVentilatorWeaningPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Ventilator weaning protocol and goals (if applicable)" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alarm Response Protocol</label>
                  <textarea value={formData.pdnVentilatorAlarmResponse} onChange={(e) => handleChange('pdnVentilatorAlarmResponse', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Response steps for ventilator alarms" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ventilator Interventions</label>
                  <textarea value={formData.pdnVentilatorIntervention} onChange={(e) => handleChange('pdnVentilatorIntervention', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Specific nursing interventions for vent management" />
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ========== PDN SEIZURE MANAGEMENT ========== */}
          {isPediatricPDN && (
            <div className="bg-yellow-50 rounded-lg border border-yellow-200">
              <div 
                className="flex items-center justify-between cursor-pointer p-4 hover:bg-yellow-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Seizure')}
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle size={18} className="text-yellow-600" />
                  <div>
                    <h4 className="text-md font-medium text-yellow-900">Seizure Management Plan</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Seizure'] ? <ChevronDown size={20} className="text-yellow-600" /> : <ChevronUp size={20} className="text-yellow-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Seizure'] ? 'hidden' : 'p-4 border-t border-yellow-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seizure Protocol</label>
                  <textarea value={formData.pdnSeizureProtocol} onChange={(e) => handleChange('pdnSeizureProtocol', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                    placeholder="Step-by-step seizure response protocol" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rescue Medication Plan</label>
                  <textarea value={formData.pdnSeizureRescuePlan} onChange={(e) => handleChange('pdnSeizureRescuePlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="When to administer rescue meds, dosing, 911 criteria" />
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ========== PDN FEEDING/NUTRITION ========== */}
          {isPediatricPDN && (
            <div className="bg-green-50 rounded-lg border border-green-200">
              <div 
                className="flex items-center justify-between cursor-pointer p-4 hover:bg-green-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Feeding')}
              >
                <div className="flex items-center gap-3">
                  <Utensils size={18} className="text-green-600" />
                  <div>
                    <h4 className="text-md font-medium text-green-900">Feeding/Nutrition Care Plan</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Feeding'] ? <ChevronDown size={20} className="text-green-600" /> : <ChevronUp size={20} className="text-green-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Feeding'] ? 'hidden' : 'p-4 border-t border-green-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feeding Method</label>
                  <select value={formData.pdnFeedingMethod} onChange={(e) => handleChange('pdnFeedingMethod', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select method</option>
                    <option value="Oral">Oral</option>
                    <option value="G-tube bolus">G-tube bolus</option>
                    <option value="G-tube continuous">G-tube continuous</option>
                    <option value="J-tube">J-tube</option>
                    <option value="NG tube">NG tube</option>
                    <option value="Combination">Combination oral and tube</option>
                    <option value="TPN">TPN</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Formula Type</label>
                  <input type="text" value={formData.pdnFormulaType} onChange={(e) => handleChange('pdnFormulaType', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 '' ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., Pediasure 1.5 Cal, Compleat Pediatric" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feeding Schedule</label>
                  <textarea value={formData.pdnFeedingSchedule} onChange={(e) => handleChange('pdnFeedingSchedule', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., 240ml q4h bolus or 45ml/hr continuous x 18 hrs" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feeding Rate</label>
                  <input type="text" value={formData.pdnFeedingRate} onChange={(e) => handleChange('pdnFeedingRate', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., 45 ml/hr" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">G-tube/J-tube Care Plan</label>
                  <textarea value={formData.pdnGtubeCarePlan} onChange={(e) => handleChange('pdnGtubeCarePlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Site care, patency checks, replacement schedule" />
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ========== EQUIPMENT SETTINGS (Phase 19 F13) ========== */}
          {isPediatricPDN && (
            <EquipmentSettingsSection
              value={formData.equipmentSettings}
              onChange={(equipmentSettings) => setFormData(prev => ({ ...prev, equipmentSettings }))}
              errors={errors}
              readOnly={isReadOnly}
              collapsed={Boolean(collapsedSections['Equipment Settings'])}
              onToggle={() => toggleSection('Equipment Settings')}
            />
          )}

          {/* ========== PDN SKILLED NURSING HOURS ========== */}
          {isPediatricPDN && (
            <div className="bg-indigo-50 rounded-lg border border-indigo-200">
              <div 
                className="flex items-center justify-between cursor-pointer p-4 hover:bg-indigo-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Hours')}
              >
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-indigo-600" />
                  <div>
                    <h4 className="text-md font-medium text-indigo-900">PDN Service Frequency (Texas Medicaid)</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Hours'] ? <ChevronDown size={20} className="text-indigo-600" /> : <ChevronUp size={20} className="text-indigo-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Hours'] ? 'hidden' : 'p-4 border-t border-indigo-200'}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours Per Day *</label>
                  <select value={formData.pdnHoursPerDay} onChange={(e) => handleChange('pdnHoursPerDay', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.pdnHoursPerDay ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select hours</option>
                    <option value="8">8 hours/day</option>
                    <option value="12">12 hours/day</option>
                    <option value="16">16 hours/day</option>
                    <option value="20">20 hours/day</option>
                    <option value="24">24 hours/day</option>
                  </select>
                  {errors.pdnHoursPerDay && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.pdnHoursPerDay}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Days Per Week *</label>
                  <select value={formData.pdnDaysPerWeek} onChange={(e) => handleChange('pdnDaysPerWeek', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.pdnDaysPerWeek ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select days</option>
                    <option value="5">5 days/week (weekdays)</option>
                    <option value="6">6 days/week</option>
                    <option value="7">7 days/week</option>
                  </select>
                  {errors.pdnDaysPerWeek && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.pdnDaysPerWeek}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Weekly Hours</label>
                  <input
                    type="text"
                    value={formData.pdnTotalWeeklyHours}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    readOnly
                    placeholder="Auto-calculated"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Shift Schedule</label>
                <input type="text" value={formData.pdnShiftSchedule} onChange={(e) => handleChange('pdnShiftSchedule', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  placeholder="e.g., 7am-7pm day shift, 7pm-7am night shift" />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours Justification</label>
                <textarea value={formData.pdnHoursJustification} onChange={(e) => handleChange('pdnHoursJustification', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="Why these hours are medically necessary" />
              </div>
              <div className="mt-4">
                <label htmlFor="rn-hours-transition" className="block text-sm font-medium text-gray-700 mb-1">Plan to decrease hours or discharge *</label>
                <textarea id="rn-hours-transition" value={formData.hoursTransitionPlan} onChange={(e) => handleChange('hoursTransitionPlan', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.hoursTransitionPlan ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                  placeholder="TMHP F00120 Section 3: e.g., reduce night hours as caregiver completes trach training" />
                {errors.hoursTransitionPlan && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.hoursTransitionPlan}</p>}
              </div>
              <div className="mt-4">
                <label htmlFor="rn-priority-problems" className="block text-sm font-medium text-gray-700 mb-1">Priority problems (two to four, one per line)</label>
                <textarea id="rn-priority-problems" value={formData.priorityProblems} onChange={(e) => handleChange('priorityProblems', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="Fills the F00120 nursing care plan summary problem list" />
              </div>
              <div className="mt-4 flex items-center space-x-6">
                <CheckboxField field="pdnNightCoverageRequired" label="Night Coverage Required" checked={formData.pdnNightCoverageRequired} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="pdnWeekendCoverageRequired" label="Weekend Coverage Required" checked={formData.pdnWeekendCoverageRequired} onChange={handleChange}  isReadOnly={isReadOnly} />
              </div>
              </div>
            </div>
          )}

          {/* ========== PDN CAREGIVER TRAINING ========== */}
          {isPediatricPDN && (
            <div className="bg-teal-50 rounded-lg border border-teal-200">
              <div 
                className="flex items-center justify-between cursor-pointer p-4 hover:bg-teal-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Caregiver Training')}
              >
                <div className="flex items-center gap-3">
                  <User size={18} className="text-teal-600" />
                  <div>
                    <h4 className="text-md font-medium text-teal-900">Caregiver/Family Training Plan</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Caregiver Training'] ? <ChevronDown size={20} className="text-teal-600" /> : <ChevronUp size={20} className="text-teal-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Caregiver Training'] ? 'hidden' : 'p-4 border-t border-teal-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Caregiver Training Plan</label>
                  <textarea value={formData.pdnCaregiverTrainingPlan} onChange={(e) => handleChange('pdnCaregiverTrainingPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Training topics and schedule for caregiver education" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Backup Caregiver Plan</label>
                  <textarea value={formData.pdnBackupCaregiverPlan} onChange={(e) => handleChange('pdnBackupCaregiverPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Plan for when primary caregiver is unavailable" />
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ========== PDN EMERGENCY PREPAREDNESS ========== */}
          {isPediatricPDN && (
            <div className="bg-rose-50 rounded-lg border border-rose-200">
              <div 
                className="flex items-center justify-between cursor-pointer p-4 hover:bg-rose-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Emergency')}
              >
                <div className="flex items-center gap-3">
                  <Shield size={18} className="text-rose-600" />
                  <div>
                    <h4 className="text-md font-medium text-rose-900">Emergency Preparedness</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Emergency'] ? <ChevronDown size={20} className="text-rose-600" /> : <ChevronUp size={20} className="text-rose-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Emergency'] ? 'hidden' : 'p-4 border-t border-rose-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact List</label>
                  <textarea value={formData.pdnEmergencyContactList} onChange={(e) => handleChange('pdnEmergencyContactList', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                    placeholder="List of emergency contacts with phone numbers" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Power Outage Plan</label>
                  <textarea value={formData.pdnPowerOutagePlan} onChange={(e) => handleChange('pdnPowerOutagePlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Backup power plan, generator availability, battery backup duration" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Failure Plan</label>
                  <textarea value={formData.pdnEquipmentFailurePlan} onChange={(e) => handleChange('pdnEquipmentFailurePlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Steps for ventilator failure, suction failure, etc." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Evacuation Plan</label>
                  <textarea value={formData.pdnEvacuationPlan} onChange={(e) => handleChange('pdnEvacuationPlan', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Emergency evacuation procedures and destination" />
                </div>
                {/* Contingency Plan Narrative — T-01B NET-NEW FIELD */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emergency / Contingency Care Plan Narrative
                    <span className="ml-1 text-xs text-gray-400">(used in Nursing Addendum Section D)</span>
                  </label>
                  <textarea
                    value={formData.contingencyPlan || ''}
                    onChange={e => handleChange('contingencyPlan', e.target.value)}
                    rows={5}
                    disabled={isReadOnly}
                    className={`w-full border border-gray-300 rounded px-3 py-2 text-sm ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="Describe the comprehensive emergency and contingency care plan for this patient..."
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Optional to save. Required to generate the Nursing Addendum without a placeholder.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <CheckboxField field="pdnHospitalGoKit" label="Hospital Go-Kit prepared and ready" checked={formData.pdnHospitalGoKit} onChange={handleChange}  isReadOnly={isReadOnly} />
                </div>
              </div>
              </div>
            </div>
          )}


          {/* ========== GOALS (Phase 19 F12: one structure for every discipline) ========== */}
          <GoalsEditor
            discipline="RN"
            goals={formData.goals}
            onChange={(goals) => setFormData(prev => ({ ...prev, goals }))}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={Boolean(collapsedSections['Nursing Goals'])}
            onToggle={() => toggleSection('Nursing Goals')}
          />

          {/* ========== NURSING INTERVENTIONS (CMS-485 Section 21) ========== */}
          <div className="bg-slate-50 rounded-lg border border-slate-200">
            <div 
              className="flex items-center justify-between cursor-pointer p-4 hover:bg-slate-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Nursing Interventions')}
            >
              <div className="flex items-center gap-3">
                <ClipboardList size={18} className="text-slate-600" />
                <div>
                  <h4 className="text-md font-medium text-slate-900">Nursing Interventions</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Nursing Interventions'] ? <ChevronDown size={20} className="text-slate-600" /> : <ChevronUp size={20} className="text-slate-600" />}
              </div>
            </div>

            <div className={collapsedSections['Nursing Interventions'] ? 'hidden' : 'p-4 border-t border-slate-200'}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Self-Care Interventions *</label>
                <textarea value={formData.interventionSelfCare} onChange={(e) => handleChange('interventionSelfCare', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.interventionSelfCare ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  rows={3} placeholder="Skilled nursing interventions for care support" />
                {errors.interventionSelfCare && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.interventionSelfCare}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Safety Interventions</label>
                <textarea value={formData.interventionSafety} onChange={(e) => handleChange('interventionSafety', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="Safety monitoring and interventions" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Medication Interventions</label>
                <textarea value={formData.interventionMedication} onChange={(e) => handleChange('interventionMedication', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="Medication administration and monitoring" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education Interventions</label>
                <textarea value={formData.interventionEducation} onChange={(e) => handleChange('interventionEducation', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder="Family/caregiver education interventions" />
              </div>
            </div>
            </div>
          </div>

          {/* ========== SKILLED NURSING ORDERS ========== */}
          <div className="bg-green-50 rounded-lg border border-green-200">
            <div 
              className="flex items-center justify-between cursor-pointer p-4 hover:bg-green-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Skilled Nursing Orders')}
            >
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-green-600" />
                <div>
                  <h4 className="text-md font-medium text-green-900">Skilled Nursing Orders</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Skilled Nursing Orders'] ? <ChevronDown size={20} className="text-green-600" /> : <ChevronUp size={20} className="text-green-600" />}
              </div>
            </div>

            <div className={collapsedSections['Skilled Nursing Orders'] ? 'hidden' : 'p-4 border-t border-green-200'}>            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SN Visit Frequency *</label>
                <select value={formData.snFrequency} onChange={(e) => handleChange('snFrequency', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.snFrequency ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select frequency</option>
                  {formConfig.isPDN ? (
                    <>
                      <option value="8 hours/day">8 hours/day PDN</option>
                      <option value="12 hours/day">12 hours/day PDN</option>
                      <option value="16 hours/day">16 hours/day PDN</option>
                      <option value="24 hours/day">24 hours/day PDN</option>
                      <option value="Custom">Custom - specify below</option>
                    </>
                  ) : (
                    <>
                      <option value="1x/week">1x/week</option>
                      <option value="2x/week">2x/week</option>
                      <option value="3x/week">3x/week</option>
                      <option value="Daily">Daily</option>
                      <option value="Custom">Custom - specify below</option>
                    </>
                  )}
                </select>
                {errors.snFrequency && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.snFrequency}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Frequency</label>
                <input type="text" value={formData.snFrequencyCustom} onChange={(e) => handleChange('snFrequencyCustom', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  placeholder="Specify custom frequency" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                <select value={formData.snDuration} onChange={(e) => handleChange('snDuration', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.snDuration ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                  <option value="">Select duration</option>
                  <option value="30 days">30 days</option>
                  <option value="60 days">60 days</option>
                  <option value="90 days">90 days</option>
                  <option value="6 months">6 months</option>
                  <option value="1 year">1 year</option>
                  <option value="Ongoing">Ongoing (recertify as needed)</option>
                </select>
                {errors.snDuration && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.snDuration}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skilled Nursing Tasks</label>
                <textarea value={formData.snTasks} onChange={(e) => handleChange('snTasks', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                  placeholder={formConfig.isPDN 
                    ? "e.g., Trach care, vent management, suctioning, medication administration, G-tube care, seizure monitoring"
                    : "e.g., Skilled assessment, medication management, wound care"} />
              </div>
            </div>
            </div>
          </div>

          {/* ========== DISCHARGE PLANNING (CMS-485 Section 11) ========== */}
          <div className="bg-emerald-50 rounded-lg border border-emerald-200">
            <div 
              className="flex items-center justify-between cursor-pointer p-4 hover:bg-emerald-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Discharge Planning')}
            >
              <div className="flex items-center gap-3">
                <Home size={18} className="text-green-600" />
                <div>
                  <h4 className="text-md font-medium text-emerald-900">Discharge Planning</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Discharge Planning'] ? <ChevronDown size={20} className="text-emerald-600" /> : <ChevronUp size={20} className="text-emerald-600" />}
              </div>
            </div>

            <div className={collapsedSections['Discharge Planning'] ? 'hidden' : 'p-4 border-t border-emerald-200'}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discharge Plan</label>
              <textarea
                value={formData.dischargePlanning}
                onChange={(e) => handleChange('dischargePlanning', e.target.value)}
                disabled={isReadOnly}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                rows={4}
                placeholder="Anticipated discharge timeline, discharge criteria (e.g., vent weaning, trach decannulation, caregiver independence), transition plans, recertification frequency. For long-term PDN: Document technology dependence and conditions that would allow discharge."
              />
              <p className="text-xs text-gray-500 mt-1">Required for CMS-485 Section 11</p>
            </div>
            </div>
          </div>

          {/* ========== PROVIDER INFORMATION ========== */}
          <ProviderSection
            keys={['providerName', 'providerCredentials', 'dateCreated']}
            overrides={{ providerCredentials: { placeholder: 'e.g., RN, BSN' } }}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections['Provider Information']}
            onToggle={() => toggleSection('Provider Information')}
          />

          {/* ========== ADDITIONAL NOTES ========== */}
          <div className="bg-gray-50 rounded-lg border border-gray-200">
            <div 
              className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Additional Notes')}
            >
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-gray-600" />
                <div>
                  <h4 className="text-md font-medium text-gray-900">Additional Notes</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Additional Notes'] ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
              </div>
            </div>

            <div className={collapsedSections['Additional Notes'] ? 'hidden' : 'p-4 border-t border-gray-200'}>
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={formData.additionalNotes} onChange={(e) => handleChange('additionalNotes', e.target.value)}
              disabled={isReadOnly}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
              placeholder="Any additional notes or special considerations..." />
            </div>
            </div>
          </div>

        </div>
      </div>
      </div>
      {draft.dialog}
    </FormPageLayout>
  );
};

export default NursingCarePlanForm;